import { useNavigate, useParams } from 'react-router-dom';
import { Progress } from '@backstage/core-components';
import { BreadcrumbEntry, useRouteRef } from '@backstage/frontend-plugin-api';
import { Container, Header } from '@backstage/ui';
import { usePulseChecks } from '../../../../hooks';
import {
  checksDetailsSubRouteRef,
  checksEditSubRouteRef,
  checksNewSubRouteRef,
  checksRouteRef,
} from '../../../../routes';
import { EmptyState } from '../../../empty-state';
import { CheckForm } from './check-form';

/** Routed page for creating (`/create-new`) or editing (`/:id/edit`) a check. */
export const PulseCheckFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const getChecksPath = useRouteRef(checksRouteRef);
  const getNewPath = useRouteRef(checksNewSubRouteRef);
  const getEditPath = useRouteRef(checksEditSubRouteRef);
  const getDetailsPath = useRouteRef(checksDetailsSubRouteRef);
  const isEdit = Boolean(id);
  const { value: checks, loading } = usePulseChecks({ cache: !isEdit });
  const initial = id ? checks?.find(check => check.id === id) : undefined;
  const isReadOnly = initial?.source === 'config';
  const checksPath = getChecksPath?.() ?? '..';
  // Return to wherever the form was opened from (list or a check's details).
  const goBack = () => navigate(-1);

  const breadcrumbLabel = isEdit ? 'Edit' : 'Create New';
  const breadcrumbHref = isEdit
    ? getEditPath?.({ id: id ?? '' }) ?? checksPath
    : getNewPath?.() ?? checksPath;

  let content: JSX.Element;
  if (isEdit && loading) {
    content = <Progress />;
  } else if (isEdit && !initial) {
    content = (
      <EmptyState
        title="Check not found"
        description={`No editable check with id "${id}" was found.`}
      />
    );
  } else {
    content = (
      <CheckForm
        key={id ?? 'new'}
        initial={initial}
        isReadOnly={isReadOnly}
        onDone={goBack}
        onCreated={createdId =>
          navigate(getDetailsPath?.({ id: createdId }) ?? checksPath, {
            state: { pulseCheckSaved: 'created' },
          })
        }
        onUpdated={updatedId =>
          navigate(getDetailsPath?.({ id: updatedId }) ?? checksPath, {
            state: { pulseCheckSaved: 'updated' },
          })
        }
      />
    );
  }

  return (
    <BreadcrumbEntry entry={{ label: breadcrumbLabel, href: breadcrumbHref }}>
      <Header
        title={isEdit ? 'Edit check' : 'Create check'}
        description="Define a Pulse check that evaluates collected facts about your catalog entities. All fields are required."
      />
      <Container>{content}</Container>
    </BreadcrumbEntry>
  );
};
